import { supabase } from '../lib/supabase'
import { handleDatabaseError, withErrorHandling, createFallbackResponse, isDatabaseSetupError, logDatabaseSetupInstructions } from '../utils/errorHandler'

// Course management service
export class CourseService {
  constructor() {
    this.categories = [
      'phishing',
      'passwords',
      'data-protection',
      'social-engineering',
      'malware',
      'network-security',
      'incident-response',
      'compliance',
      'general'
    ]
    
    this.difficultyLevels = ['beginner', 'intermediate', 'advanced']
  }

  // Create a new course
  async createCourse(courseData) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Validate required fields
    if (!courseData.title || !courseData.category || !courseData.difficulty) {
      throw new Error('Title, category, and difficulty are required')
    }

    // Validate category and difficulty
    if (!this.categories.includes(courseData.category)) {
      throw new Error(`Invalid category. Must be one of: ${this.categories.join(', ')}`)
    }

    if (!this.difficultyLevels.includes(courseData.difficulty)) {
      throw new Error(`Invalid difficulty. Must be one of: ${this.difficultyLevels.join(', ')}`)
    }

    const course = {
      title: courseData.title,
      description: courseData.description || '',
      category: courseData.category,
      difficulty: courseData.difficulty,
      thumbnail_url: courseData.thumbnailUrl || null,
      estimated_duration: courseData.estimatedDuration || 0,
      status: courseData.isPublished ? 'published' : 'draft',
      prerequisites: courseData.prerequisites || [],
      learning_objectives: courseData.learningObjectives || [],
      tags: courseData.tags || [],
      created_by: user.id
    }

    return await withErrorHandling(async () => {
      const { data, error } = await supabase
        .from('courses')
        .insert([course])
        .select()
        .single()

      if (error) {
        throw error
      }

      return data
    }, 'course creation')
  }

  // Update course
  async updateCourse(courseId, updates) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Validate category and difficulty if provided
    if (updates.category && !this.categories.includes(updates.category)) {
      throw new Error(`Invalid category. Must be one of: ${this.categories.join(', ')}`)
    }

    if (updates.difficulty && !this.difficultyLevels.includes(updates.difficulty)) {
      throw new Error(`Invalid difficulty. Must be one of: ${this.difficultyLevels.join(', ')}`)
    }

    return await withErrorHandling(async () => {
      const { data, error } = await supabase
        .from('courses')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', courseId)
        .eq('created_by', user.id)
        .select()
        .single()

      if (error) {
        throw error
      }

      return data
    }, 'course update')
  }

  // Delete course
  async deleteCourse(courseId) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Check if course has enrolled users
    const { data: enrollments, error: enrollmentError } = await supabase
      .from('user_course_progress')
      .select('id')
      .eq('course_id', courseId)
      .limit(1)

    if (enrollmentError) {
      throw enrollmentError
    }

    if (enrollments && enrollments.length > 0) {
      throw new Error('Cannot delete course with enrolled users. Unpublish the course instead.')
    }

    // Delete associated videos first
    const { error: videosError } = await supabase
      .from('videos')
      .delete()
      .eq('course_id', courseId)

    if (videosError) {
      throw videosError
    }

    // Delete the course
    const { error } = await supabase
      .from('courses')
      .delete()
      .eq('id', courseId)
      .eq('created_by', user.id)

    if (error) {
      throw error
    }

    return { success: true }
  }

  // Get course by ID with videos
  async getCourse(courseId, includeVideos = true) {
    let query = supabase
      .from('courses')
      .select('*')
      .eq('id', courseId)
      .single()

    const { data: course, error } = await query

    if (error) {
      throw error
    }

    if (includeVideos) {
      const { data: videos, error: videosError } = await supabase
        .from('videos')
        .select('*')
        .eq('course_id', courseId)
        .order('order_index', { ascending: true })

      if (videosError) {
        throw videosError
      }

      course.videos = videos || []
    }

    return course
  }

  // Get all courses with filters
  async getCourses(filters = {}) {
    try {
      let query = supabase
        .from('courses')
        .select(`
          *,
          videos!course_id (
            id,
            title,
            duration
          )
        `)

      // Apply filters
      if (filters.category) {
        query = query.eq('category', filters.category)
      }

      if (filters.difficulty) {
        query = query.eq('difficulty', filters.difficulty)
      }

      if (filters.isPublished !== undefined) {
        query = query.eq('status', filters.isPublished ? 'published' : 'draft')
      }

      if (filters.createdBy) {
        query = query.eq('created_by', filters.createdBy)
      }

      if (filters.search) {
        query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`)
      }

      if (filters.tags && filters.tags.length > 0) {
        query = query.overlaps('tags', filters.tags)
      }

      // Apply sorting
      const sortBy = filters.sortBy || 'created_at'
      const sortOrder = filters.sortOrder || 'desc'
      query = query.order(sortBy, { ascending: sortOrder === 'asc' })

      // Apply pagination
      if (filters.limit) {
        query = query.limit(filters.limit)
      }

      if (filters.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1)
      }

      const { data, error } = await query

      if (error) {
        throw error
      }

      // Calculate total duration for each course
      return data.map(course => ({
        ...course,
        total_duration: course.videos?.reduce((sum, video) => sum + (video.duration || 0), 0) || 0,
        video_count: course.videos?.length || 0
      }))
    } catch (error) {
      if (isDatabaseSetupError(error)) {
        logDatabaseSetupInstructions()
        return []
      }
      throw handleDatabaseError(error, 'fetching courses')
    }
  }

  // Enroll user in course
  async enrollInCourse(courseId) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Check if course exists and is published
    const { data: course, error: courseError } = await supabase
      .from('courses')
      .select('id, status')
      .eq('id', courseId)
      .single()

    if (courseError || !course) {
      throw new Error('Course not found')
    }

    if (course.status !== 'published') {
      throw new Error('Course is not published')
    }

    // Check if already enrolled
    const { data: existing, error: existingError } = await supabase
      .from('user_course_progress')
      .select('id')
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .single()

    if (existing) {
      throw new Error('Already enrolled in this course')
    }

    // Create enrollment record
    const { data, error } = await supabase
      .from('user_course_progress')
      .insert([{
        user_id: user.id,
        course_id: courseId,
        progress_percentage: 0,
        started_at: new Date().toISOString()
      }])
      .select()
      .single()

    if (error) {
      throw error
    }

    return data
  }

  // Unenroll from course
  async unenrollFromCourse(courseId) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Delete enrollment record
    const { error } = await supabase
      .from('user_course_progress')
      .delete()
      .eq('user_id', user.id)
      .eq('course_id', courseId)

    if (error) {
      throw error
    }

    // Delete video progress records
    await supabase
      .from('user_video_progress')
      .delete()
      .eq('user_id', user.user.id)
      .in('video_id', 
        supabase
          .from('videos')
          .select('id')
          .eq('course_id', courseId)
      )

    return { success: true }
  }

  // Get user's enrolled courses
  async getUserEnrolledCourses(userId = null) {
    const { data: { user } } = await supabase.auth.getUser()
    const targetUserId = userId || user?.id
    
    if (!targetUserId) {
      throw new Error('User not authenticated')
    }

    const { data, error } = await supabase
      .from('user_course_progress')
      .select(`
        *,
        courses (
          id,
          title,
          description,
          category,
          difficulty,
          thumbnail_url,
          author,
          created_by
        )
      `)
      .eq('user_id', targetUserId)
      .order('started_at', { ascending: false })

    if (error) {
      throw error
    }

    return data
  }

  // Get user's created courses
  async getUserCreatedCourses(userId = null) {
    const { data: { user } } = await supabase.auth.getUser()
    const targetUserId = userId || user?.id
    
    if (!targetUserId) {
      throw new Error('User not authenticated')
    }

    return this.getCourses({ createdBy: targetUserId })
  }

  // Update course progress
  async updateCourseProgress(courseId) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Call the database function to calculate progress
    const { data, error } = await supabase
      .rpc('calculate_course_progress', {
        p_user_id: user.id,
        p_course_id: courseId
      })

    if (error) {
      throw error
    }

    return data
  }

  // Mark video as watched
  async markVideoAsWatched(videoId, watchedDuration = null) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Get video info
    const { data: video, error: videoError } = await supabase
      .from('videos')
      .select('id, duration, course_id')
      .eq('id', videoId)
      .single()

    if (videoError || !video) {
      throw new Error('Video not found')
    }

    // Upsert video progress
    const { data, error } = await supabase
      .from('user_video_progress')
      .upsert({
        user_id: user.id,
        video_id: videoId,
        watched_duration: watchedDuration || video.duration,
        completed: true,
        last_watched_at: new Date().toISOString()
      })
      .select()
      .single()

    if (error) {
      throw error
    }

    // Update course progress
    if (video.course_id) {
      await this.updateCourseProgress(video.course_id)
    }

    return data
  }

  // Get course statistics
  async getCourseStatistics(courseId) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Get enrollment count
    const { count: enrollmentCount, error: enrollmentError } = await supabase
      .from('user_course_progress')
      .select('*', { count: 'exact', head: true })
      .eq('course_id', courseId)

    if (enrollmentError) {
      throw enrollmentError
    }

    // Get completion count
    const { count: completionCount, error: completionError } = await supabase
      .from('user_course_progress')
      .select('*', { count: 'exact', head: true })
      .eq('course_id', courseId)
      .eq('completed', true)

    if (completionError) {
      throw completionError
    }

    // Get average progress
    const { data: progressData, error: progressError } = await supabase
      .from('user_course_progress')
      .select('progress_percentage')
      .eq('course_id', courseId)

    if (progressError) {
      throw progressError
    }

    const averageProgress = progressData.length > 0 
      ? progressData.reduce((sum, p) => sum + p.progress_percentage, 0) / progressData.length
      : 0

    return {
      enrollmentCount: enrollmentCount || 0,
      completionCount: completionCount || 0,
      completionRate: enrollmentCount > 0 ? (completionCount / enrollmentCount) * 100 : 0,
      averageProgress: Math.round(averageProgress)
    }
  }

  // Search courses
  async searchCourses(query, filters = {}) {
    return this.getCourses({
      ...filters,
      search: query,
      isPublished: true
    })
  }

  // Get featured courses
  async getFeaturedCourses(limit = 6) {
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .eq('status', 'published')
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) {
      throw error
    }

    return data
  }

  // Get course categories with counts
  async getCategoriesWithCounts() {
    const { data, error } = await supabase
      .from('courses')
      .select('category')
      .eq('status', 'published')

    if (error) {
      throw error
    }

    const categoryCounts = data.reduce((acc, course) => {
      acc[course.category] = (acc[course.category] || 0) + 1
      return acc
    }, {})

    return this.categories.map(category => ({
      name: category,
      count: categoryCounts[category] || 0,
      label: category.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ')
    }))
  }
}

// Create singleton instance
export const courseService = new CourseService()

// Utility functions
export const formatCategoryName = (category) => {
  return category.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ')
}

export const getDifficultyColor = (difficulty) => {
  const colors = {
    beginner: 'green',
    intermediate: 'yellow',
    advanced: 'red'
  }
  return colors[difficulty] || 'gray'
}

export const getCategoryIcon = (category) => {
  const icons = {
    'phishing': '🎣',
    'passwords': '🔐',
    'data-protection': '🛡️',
    'social-engineering': '👥',
    'malware': '🦠',
    'network-security': '🌐',
    'incident-response': '🚨',
    'compliance': '📋',
    'general': '📚'
  }
  return icons[category] || '📚'
}